import _ from 'lodash';
import { useParams } from 'react-router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import ApplicationContext from '../../../ApplicationContext';
import CloseButton from '../../../ui/CloseButton';
import { JobNames } from '../../../framework.core/JobNames';
import { AcctDetail } from '../../../fms.core/AcctDetail';
import BTLoadPlanBillingsDetails from './implementation/BTLoadPlanBillingsDetails';
import constants from '../../../constants';
import useJobMenu from '../../../hooks/useJobMenu';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';
import { ApiResult } from '../../../framework.core/data/ApiResult';
import { getTooltipValue } from '../../../util';
import { BtLoad } from '../../../fms.core/BtLoad';
import { SalesCostSummary } from '../../common/SalesCostEntry';

// interface BillingInfoStorageKeys {
//   modelStorage: string,
// };

interface SummaryStorageKeys {
  modelSummaryStorage: string,
};

interface BillingBtLoadInfoStorageKeys {
  modelBtLoadStorage: string
};

interface BTLoadPlanBillingsViewParams {
  id: string | undefined,
};

interface BillingInfoLoadedParams {
  // billingInfo: Array<AcctDetail>,
  btloadInfo: BtLoad,
  close: () => {},
  summary: SalesCostSummary,
};

function BTLoadPlanBillingsDetailsImpl(params: BTLoadPlanBillingsViewParams) {
  const id = useRef(params.id);
  const title = 'View Billings (ODL)';

  // const { modelStorage } = getBillingInfoStorageKeys(id.current);
  const { modelBtLoadStorage } = getBtLoadStorageKeys(id.current);
  const { modelSummaryStorage } = getSummaryStorageKeys(id.current);

  const {
    goBack
  } = useJobMenu(title, constants.operations.load, null, true, cleanup);
  //

  const { dirtyCleanup } = useDirtyCheck(modelBtLoadStorage, ['updated', 'updatedBy']);

  // const [inStorage, setInStorage, , ] = useTypedFastStorage<Array<AcctDetail>>(modelStorage, null);
  const [btLoadInStorage, setBtLoadInStorage, , ] = useTypedFastStorage<BtLoad>(modelBtLoadStorage, null);
  const [summaryStorage, setSummaryStorage, , ] = useTypedFastStorage<SalesCostSummary>(modelSummaryStorage, {
      billSummary: [],
      invoiceSummary: [],
      crdbSummary: [],
    });

  const appContext = useContext(ApplicationContext);

  const [initialized, setInitialized] = useState(false);

  const [showGlobalOkDialog] = useGlobalOkDialog();
  useEffect(() => {
    initialize();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    //If initialized is true, then form's tab button is active
    const activeClass = appContext.useDarkMode ? 'e-job-btn-active-darkmode' : 'e-job-btn-active';
    const activeTabButton = document.getElementsByClassName(activeClass)[0] as HTMLInputElement;
    if (activeTabButton && btLoadInStorage) {
      activeTabButton.dataset.tooltip = getTooltipValue(btLoadInStorage['loadNo'], JobNames.BTLoadPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  // function getBillingInfoStorageKeys(id: string | undefined): BillingInfoStorageKeys {
  //   return { modelStorage: `common-billinginfo-btload-view${id}` }
  // }
  function getBtLoadStorageKeys(id: string | undefined): BillingBtLoadInfoStorageKeys {
    return { modelBtLoadStorage: `common-billinginfo-btloaddetail-view${id}` }
  }
  function getSummaryStorageKeys(id: string | undefined ): SummaryStorageKeys {
    return { modelSummaryStorage: `common-summary-btloaddetail-view${id}` }
  }

  function cleanup() {
    dirtyCleanup();
  }

  function close() {
    goBack();
    return true;
  }

  // async function loadBillingData() {
  //   const apiResult = await appContext.api.getJobAcctDetails(JobNames.BTLoadPlan, Number(id.current));

  //   if (!apiResult) {
  //     await showGlobalOkDialog(<p>Load billings not found</p>,false,null,'warning');
  //     goBack();
  //     return false;
  //   }
  //   setInStorage(apiResult);

  //   return true;
  // }

  async function loadBtLoadData() {
    const apiResult: ApiResult<BtLoad> = await appContext.api.getJobDetail(JobNames.BTLoadPlan, { id: id.current });
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult ? <p>{apiResult.message}</p> : <p>Load not found</p>,false,null,'warning');
      goBack();
      return false;
    }
    const btLoad = new BtLoad(apiResult.result); //save-sqlinfo
    setBtLoadInStorage(_.cloneDeep(btLoad));

    return true;
  }

  async function initializeSummary() {
    const apiResult = await appContext.api.getJobSalesCostSummary(JobNames.BTLoadPlan, id.current);

    if (apiResult && apiResult.success) {
      setSummaryStorage(apiResult.result);
    } 
  }

  async function initialize() {
    if (!btLoadInStorage) {
      await loadBtLoadData();
      await initializeSummary();
    }

    setInitialized(true);
  }

  if (!initialized || !btLoadInStorage || !summaryStorage) {
    return null;
  }

  return (
    <BillingInfoInitializedImpl
      close={close}
      // billingInfo={inStorage}
      btloadInfo={btLoadInStorage}
      summary={summaryStorage}
    />
  );
}

function BillingInfoInitializedImpl({
  // billingInfo,
  btloadInfo,
  summary,
  close,
}: BillingInfoLoadedParams) {

  useFunctionKeys({ onEsc: cancel });

  function cancel() {
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }
    
    close();
    return true;
  }

  return (
    <BTLoadPlanBillingsDetails
      commands={
        <>
          <div className="ifms-left-separator-light-gray" />
          <div className="ifms-common-bar-element">
            <CloseButton mode='light' onClick={cancel} />
          </div>
        </>
      }
      // billingInfo={billingInfo}
      summary={summary}
      btloadInfo={btloadInfo}
    />
  );
}

const BTLoadPlanBillingsView = () => {
  const { id } = useParams<BTLoadPlanBillingsViewParams>();

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */
  return (
    <BTLoadPlanBillingsDetailsImpl id={id} key={`btload-${id}-billings`}/>
  );
};
export default BTLoadPlanBillingsView;
